/**
 * Simple markdown to HTML renderer for AI-generated content.
 * Converts ### headers, **bold**, *italic*, --- hr, and lists.
 * Returns sanitized HTML (basic — no script/event tags).
 */

const TAG_STRIP_RE = /<(script|style|iframe|object|embed|form|input)[^>]*>[\s\S]*?<\/\1>|<(script|style|iframe|object|embed|form|input)[^>]*\/?>/gi;
const EVENT_ATTR_RE = /\son\w+\s*=\s*["'][^"']*["']/gi;
const JS_PROTO_RE = /javascript\s*:/gi;

function sanitize(html: string): string {
  return html
    .replace(TAG_STRIP_RE, '')
    .replace(EVENT_ATTR_RE, '')
    .replace(JS_PROTO_RE, '');
}

export function renderMarkdown(md: string): string {
  let html = md
    // Escape HTML entities first (but preserve our own tags)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Horizontal rules
    .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid rgba(212,175,55,0.2);margin:16px 0">')
    // Headers: ### text → h3
    .replace(/^### (.+)$/gm, '<h3 style="color:#d4af37;font-size:16px;margin:16px 0 8px;font-family:var(--font-display);font-style:italic">$1</h3>')
    // Headers: ## text → h2
    .replace(/^## (.+)$/gm, '<h2 style="color:#d4af37;font-size:18px;margin:20px 0 10px;font-family:var(--font-display);font-style:italic">$1</h2>')
    // Headers: # text → h1
    .replace(/^# (.+)$/gm, '<h1 style="color:#d4af37;font-size:20px;margin:24px 0 12px;font-family:var(--font-display);font-style:italic">$1</h1>')
    // Bold + Italic: ***text*** or **_text_**
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    // Bold: **text**
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* or _text_
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    // Unordered lists: - item or * item or • item
    .replace(/^[\-\*•] (.+)$/gm, '<li style="margin:4px 0 4px 16px;list-style:disc;color:#ccc">$1</li>')
    // Numbered lists: 1. item
    .replace(/^\d+\. (.+)$/gm, '<li style="margin:4px 0 4px 16px;list-style:decimal;color:#ccc">$1</li>')
    // Wrap consecutive <li> in <ul>
    .replace(/((?:<li[^>]*>.*<\/li>\n?)+)/g, '<ul style="padding-left:20px;margin:8px 0">$1</ul>')
    // Paragraphs: double newlines
    .replace(/\n\n/g, '</p><p style="margin:8px 0;color:#e0e0e0;font-size:15px;line-height:1.8">')
    // Single newlines → br (but not after block elements)
    .replace(/\n/g, '<br>')
    // Clean up br before/after block elements
    .replace(/<br>\s*(<h[1-3]|<ul|<li|<hr)/g, '$1')
    .replace(/(<\/h[1-3]>|<\/ul>|<\/li>|<hr[^>]*>)\s*<br>/g, '$1');

  // Wrap in paragraph
  html = '<p style="margin:8px 0;color:#e0e0e0;font-size:15px;line-height:1.8">' + html + '</p>';

  return sanitize(html);
}
